import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import type { SxProps, Theme } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { RHFDatePickerField } from '@openx/components-core/lib/DatePickerField/RHFDatePickerField.tsx';
import { ToggleButtonGroupDesignSystemScope } from '@openx/components-core/lib/ToggleButtonGroupField/ToggleButtonGroupDesignSystemScope.tsx';
import { datePickerLocale } from '@openx/components-core/lib/utils/datePickerLocale.ts';
import { addDays, isAfter, isValid, subDays } from 'date-fns';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { date, object } from 'yup';
import { TIME_PERIOD_CONFIGS, type TimePeriod, timePeriod } from '../constants.ts';
import { getFunnelSupportedDateRange } from '../utils/getFunnelSupportedDateRange.ts';

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

export interface TroubleshootingDateFilterProps {
  selectedPeriod: TimePeriod;
  customDateRange?: DateRange;
  onPeriodChange: (period: TimePeriod, dateRange?: DateRange) => void;
  lastUpdated?: string | null;
}

interface TroubleshootingFormData {
  startDate: Date | null;
  endDate: Date | null;
}

const styles: { [key: string]: SxProps<Theme> } = {
  button: {
    height: '32px',
  },
  buttonContainer: { paddingTop: 2 },
};

export function TroubleshootingDateFilter({
  selectedPeriod,
  customDateRange,
  onPeriodChange,
}: TroubleshootingDateFilterProps) {
  const { t } = useTranslation();

  const { maxDate, minDate, isDateRangeExceeded } = useMemo(() => getFunnelSupportedDateRange(), []);

  // to be unified in future with DealTroubleshootingFunnel date form in next PRs
  const validationSchema = useMemo(
    () =>
      object().shape({
        startDate: date()
          .required(t('Start Date is required'))
          .typeError(t('Start date should be in format YYYY-MM-DD'))
          .min(
            minDate,
            isDateRangeExceeded
              ? t('Please select a date after November 13, 2024.')
              : t('Start date must be within the last 180 days.')
          )
          .test({
            name: 'start-before-end',
            message: t('Start Date cannot be after End Date.'),
            test: function (startDateValue) {
              const { endDate } = this.parent;
              if (!startDateValue || !endDate) return true;
              if (isValid(endDate) && isValid(startDateValue)) {
                return !isAfter(startDateValue, endDate);
              }
              return true;
            },
          }),
        endDate: date()
          .required(t('End Date is required'))
          .typeError(t('End date should be in format YYYY-MM-DD'))
          .max(addDays(maxDate, 1), t("End date cannot exceed yesterday's date.")),
      }),
    [t, minDate, maxDate, isDateRangeExceeded]
  );

  const methods = useForm<TroubleshootingFormData>({
    defaultValues: {
      startDate: customDateRange?.startDate || null,
      endDate: customDateRange?.endDate || null,
    },
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const {
    watch,
    setValue,
    trigger,
    formState: { isValid: isFormValid },
  } = methods;

  const observableStartDate = watch('startDate');
  const observableEndDate = watch('endDate');
  useEffect(() => {
    if (customDateRange) {
      setValue('startDate', customDateRange.startDate);
      setValue('endDate', customDateRange.endDate);
    }
  }, [customDateRange, setValue]);

  const handlePeriodChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newPeriod: TimePeriod | null) => {
      if (newPeriod && newPeriod !== timePeriod.Custom) {
        onPeriodChange(newPeriod);
      } else if (newPeriod === timePeriod.Custom) {
        const defaultRange = customDateRange || {
          startDate: subDays(new Date(), 7),
          endDate: subDays(new Date(), 1),
        };
        setValue('startDate', defaultRange.startDate);
        setValue('endDate', defaultRange.endDate);
        onPeriodChange(timePeriod.Custom, defaultRange);
      }
    },
    [onPeriodChange, customDateRange, setValue]
  );

  const handleDateChange = useCallback(
    (value: Date | null, field: 'startDate' | 'endDate') => {
      setValue(field, value, { shouldDirty: true, shouldValidate: true });

      if (field === 'startDate') {
        trigger('endDate');
      } else if (field === 'endDate') {
        trigger('startDate');
      }
    },
    [setValue, trigger]
  );

  const handleOkClick = useCallback(() => {
    if (observableStartDate && observableEndDate) {
      onPeriodChange(timePeriod.Custom, {
        startDate: observableStartDate,
        endDate: observableEndDate,
      });
    }
  }, [onPeriodChange, observableStartDate, observableEndDate]);

  return (
    <FormProvider {...methods}>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', overflow: 'visible' }}
        data-test="troubleshooting-time-filter"
        data-testid="funnel-dates"
      >
        <Grid
          container
          sx={{
            alignItems: 'center',
            overflow: 'visible',
            alignSelf: 'flex-start',
          }}
        >
          <ToggleButtonGroupDesignSystemScope>
            <ToggleButtonGroup
              value={selectedPeriod}
              exclusive
              onChange={handlePeriodChange}
              aria-label={t('Time period filter')}
              data-test="toggle-button-group"
              sx={{ flexWrap: 'wrap', height: 'auto' }}
            >
              {TIME_PERIOD_CONFIGS.map(config => (
                <ToggleButton
                  key={config.value}
                  value={config.value}
                  data-test={config.testId}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t(config.labelKey)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </ToggleButtonGroupDesignSystemScope>
        </Grid>
        {selectedPeriod === timePeriod.Custom && (
          <Box data-test="custom-dates" sx={{ marginTop: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={datePickerLocale}>
              <Grid container spacing={1}>
                <Grid size={4}>
                  <RHFDatePickerField
                    name="startDate"
                    label={t('Start date')}
                    data-test="startDate"
                    maxDate={observableEndDate || maxDate}
                    minDate={minDate}
                    onChange={value => handleDateChange(value, 'startDate')}
                  />
                </Grid>
                <Grid size={4}>
                  <RHFDatePickerField
                    name="endDate"
                    label={t('End date')}
                    data-test="endDate"
                    maxDate={maxDate}
                    minDate={observableStartDate || minDate}
                    onChange={value => handleDateChange(value, 'endDate')}
                  />
                </Grid>
                <Grid size={2}>
                  <Box sx={styles.buttonContainer}>
                    <Button
                      data-test="button-get-data"
                      onClick={handleOkClick}
                      sx={styles.button}
                      disabled={!isFormValid}
                      color="primary"
                    >
                      {t('OK')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>
        )}
      </Box>
    </FormProvider>
  );
}
